// Timesheet correction in site-local time with explicit overnight and clock-change handling.
import React, { useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";

export const localParts = (iso, tz, fallbackDate) => {
  if (!iso) return { date: fallbackDate, time: "" };
  if (/^\d{2}:\d{2}$/.test(iso)) return { date: fallbackDate, time: iso };
  const d = new Date(iso);
  if (isNaN(d)) return { date: fallbackDate, time: "" };
  const p = Object.fromEntries(new Intl.DateTimeFormat("en-GB", { timeZone: tz, year: "numeric", month: "2-digit", day: "2-digit", hour: "2-digit", minute: "2-digit", hourCycle: "h23" }).formatToParts(d).map((x) => [x.type, x.value]));
  return { date: `${p.year}-${p.month}-${p.day}`, time: `${p.hour}:${p.minute}` };
};

const addDay = (d) => { const x = new Date(`${d}T12:00:00Z`); x.setUTCDate(x.getUTCDate() + 1); return x.toISOString().slice(0, 10); };

function Occurrence({ which, value, onChange }) {
  return (
    <label className="text-[11px] text-amber-700">{which} time occurs twice (clocks went back)
      <select className="block border rounded px-2 h-8 text-xs" value={value || ""} onChange={(e) => onChange(e.target.value)} data-testid={`ts-${which.toLowerCase()}-occurrence`}>
        <option value="">Choose…</option><option value="first">First occurrence (before change)</option><option value="second">Second occurrence (after change)</option>
      </select></label>
  );
}

export function TimesheetEditForm({ ts, tz, onDone, onCancel }) {
  const s = localParts(ts.actual_start || ts.scheduled_start, tz, ts.date);
  const e = localParts(ts.actual_finish || ts.scheduled_end, tz, ts.date);
  const b = ts.breaks || {};
  const [f, setF] = useState({ start_date: s.date, start_time: s.time, finish_time: e.time, unpaid: b.unpaid_break_minutes ?? 0, paid: b.paid_break_minutes ?? 0 });
  const [occ, setOcc] = useState({});
  const [ask, setAsk] = useState({});
  const set = (k, v) => setF({ ...f, [k]: v });
  const overnight = f.finish_time && f.start_time && f.finish_time <= f.start_time;
  const finishDate = overnight ? addDay(f.start_date) : f.start_date;
  const save = async () => {
    try {
      await api.put(`/workforce/timesheets/${ts.timesheet_id}`, { local: { start_date: f.start_date, start_time: f.start_time, finish_time: f.finish_time, start_occurrence: occ.start, finish_occurrence: occ.finish }, actual_break_minutes: f.unpaid, actual_paid_break_minutes: f.paid });
      toast.success("Timesheet updated"); onDone();
    } catch (err) {
      const d = err?.response?.data?.detail || "Not saved — your entries are kept";
      if (/occurs twice/.test(d)) setAsk({ ...ask, [d.startsWith("Start") ? "start" : "finish"]: true });
      toast.error(d);
    }
  };
  return (
    <div className="border rounded-lg p-3 space-y-2" data-testid="ts-edit-form">
      <div className="text-[11px] text-slate-500" data-testid="ts-edit-tz">Times are in site time: <b>{tz}</b>. A finish at or before the start time is treated as the next day.</div>
      {ts.break_classification_conflict && <div className="text-[11px] text-amber-700" data-testid="ts-edit-conflict-note">Paid and unpaid break entries overlap by {ts.break_conflict_minutes}m. Set the paid and unpaid minutes below to resolve; the original clock entries are kept.</div>}
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-2 items-end">
        <label className="text-[11px] text-slate-500">Start date<Input type="date" className="h-8" value={f.start_date} onChange={(x) => set("start_date", x.target.value)} data-testid="ts-start-date" /></label>
        <label className="text-[11px] text-slate-500">Start time<Input type="time" className="h-8" value={f.start_time} onChange={(x) => set("start_time", x.target.value)} data-testid="ts-start-time" /></label>
        <label className="text-[11px] text-slate-500">Finish time<Input type="time" className="h-8" value={f.finish_time} onChange={(x) => set("finish_time", x.target.value)} data-testid="ts-finish-time" /></label>
        <label className="text-[11px] text-slate-500">Unpaid break (min)<Input type="number" min="0" className="h-8" value={f.unpaid} onChange={(x) => set("unpaid", x.target.value)} data-testid="ts-actual_break_minutes" /></label>
        <label className="text-[11px] text-slate-500">Paid break (min)<Input type="number" min="0" className="h-8" value={f.paid} onChange={(x) => set("paid", x.target.value)} data-testid="ts-actual_paid_break_minutes" /></label>
      </div>
      <div className="text-xs text-slate-600" data-testid="ts-edit-finish-date">Finishes {finishDate}{overnight ? " (overnight — next day)" : ""}</div>
      <div className="flex flex-wrap gap-3">
        {ask.start && <Occurrence which="Start" value={occ.start} onChange={(v) => setOcc({ ...occ, start: v })} />}
        {ask.finish && <Occurrence which="Finish" value={occ.finish} onChange={(v) => setOcc({ ...occ, finish: v })} />}
      </div>
      <div className="flex gap-2"><Button size="sm" onClick={save} disabled={!f.start_date || !f.start_time || !f.finish_time} data-testid="ts-save">Save</Button>
        <Button size="sm" variant="ghost" onClick={onCancel} data-testid="ts-cancel">Cancel</Button></div>
    </div>
  );
}
